import React from 'react';
import { Navigate } from 'react-router-dom';
import { useCan } from '../../hooks/useCan';
import { useAuth } from '../../hooks/useAuth';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { Box } from '@mui/material';

/**
 * ProtectedRoute - Route wrapper that enforces permission-based access control
 * 
 * @param {React.ReactNode} children - Child components to render if authorized
 * @param {Object|Array} requiredPermission - Permission requirements
 * @param {string} requiredPermission.section - Permission section (e.g., 'admin', 'patients')
 * @param {string} requiredPermission.level - Access level ('read' or 'write')
 * @param {string} redirectTo - Path to redirect unauthorized users (default: '/dashboard')
 * @param {boolean} requireAll - If multiple permissions, require all vs any (default: false = any)
 * 
 * @example
 * // Single permission check
 * <ProtectedRoute requiredPermission={{ section: 'admin', level: 'read' }}>
 *   <AdminPage />
 * </ProtectedRoute>
 * 
 * @example
 * // Multiple permissions (user needs ANY of these)
 * <ProtectedRoute requiredPermission={[
 *   { section: 'inventory', level: 'read' },
 *   { section: 'admin', level: 'read' }
 * ]}>
 *   <InventoryPage />
 * </ProtectedRoute>
 * 
 * @example
 * // Multiple permissions (user needs ALL of these)
 * <ProtectedRoute 
 *   requiredPermission={[
 *     { section: 'reporting', level: 'read' },
 *     { section: 'patients', level: 'read' }
 *   ]}
 *   requireAll={true}
 * >
 *   <ReportingPage />
 * </ProtectedRoute>
 */
const ProtectedRoute = ({ 
  children, 
  requiredPermission = null, 
  requiredModule = null,
  redirectTo = '/forbidden',
  requireAll = false 
}) => {
  const can = useCan();
  const moduleAccess = useModuleAccess();
  const { permissions, modules, loading } = useAuth();

  // Wait for permissions to load before checking authorization
  // This prevents false negatives during page refresh
  if (loading || permissions === null || (requiredModule && modules === null)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        Loading...
      </Box>
    );
  }

  if (requiredModule && !moduleAccess.isEnabled(requiredModule)) {
    return <Navigate to={redirectTo} replace />;
  }

  // If no permission requirement, allow access (public route within auth boundary)
  if (!requiredPermission) {
    return children;
  }

  // Handle single permission check
  if (!Array.isArray(requiredPermission)) {
    const { section, level = 'read', action } = requiredPermission;
    const hasAccess = action 
      ? can.section(section, { action })
      : can.section(section, { level });
    
    if (!hasAccess) {
      return <Navigate to={redirectTo} replace />;
    }
    
    return children;
  }

  // Handle multiple permissions
  const hasAccess = requireAll 
    ? can.all(requiredPermission.map(p => ({ section: p.section, level: p.level || 'read' })))
    : can.any(requiredPermission.map(p => ({ section: p.section, level: p.level || 'read' })));

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default ProtectedRoute;
